/**
 * Pure helpers for checkbox list selection — used by Routes/All, Routes/Detail,
 * and Leads/List so click + shift-click behave identically across the app.
 *
 * Shift-click range model is Gmail/Excel-style:
 *   - The clicked row's resulting state determines whether the range is
 *     selected or deselected. So if you click an unchecked row with shift,
 *     every row between the anchor and the clicked row is checked.
 */

export function nextSelectionFromClick(
  id: string,
  shiftKey: boolean,
  ids: string[],
  lastClickedId: string | null,
  current: Set<string>,
): Set<string> {
  const next = new Set(current);

  if (shiftKey && lastClickedId && lastClickedId !== id) {
    const a = ids.indexOf(lastClickedId);
    const b = ids.indexOf(id);
    if (a >= 0 && b >= 0) {
      const [lo, hi] = a < b ? [a, b] : [b, a];
      const shouldSelect = !current.has(id);
      for (let i = lo; i <= hi; i++) {
        if (shouldSelect) next.add(ids[i]);
        else next.delete(ids[i]);
      }
      return next;
    }
  }

  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

/**
 * Header-checkbox toggle. Gmail-style "indeterminate" semantics:
 *   - none selected → select all on page
 *   - some OR all selected → deselect everything on page
 *
 * The asymmetric behaviour is intentional. A header that shows indeterminate
 * ("some selected") should clear the page when clicked — that's the most
 * common follow-up after bulk-selecting then realising you wanted to start
 * over. Re-selecting is one more click; you never "lose" anything you can't
 * recover with another header click.
 */
export function togglePageSelection(
  ids: string[],
  current: Set<string>,
): Set<string> {
  const next = new Set(current);
  const anySelected = ids.some((id) => next.has(id));
  if (anySelected) ids.forEach((id) => next.delete(id));
  else ids.forEach((id) => next.add(id));
  return next;
}

/** Tri-state for the header checkbox: none / some / all on the visible page. */
export function pageSelectionState(
  ids: string[],
  current: Set<string>,
): "none" | "some" | "all" {
  if (ids.length === 0) return "none";
  let hit = 0;
  for (const id of ids) if (current.has(id)) hit++;
  if (hit === 0) return "none";
  if (hit === ids.length) return "all";
  return "some";
}
